import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Send, Check } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { toast } from "sonner";
import { DEFAULT_AVATAR } from "../lib/avatars";

const av = (p) => p || DEFAULT_AVATAR;

export default function ShareModal({ sessionType, sessionId, onClose }) {
  const { t } = useI18n();
  const [friends, setFriends] = useState([]);
  const [picked, setPicked] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/friends").then(({ data }) => setFriends(data.friends || [])).catch(() => {});
  }, []);

  const toggle = (id) => setPicked((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const send = async () => {
    if (!picked.length) return;
    setSending(true);
    try {
      await api.post("/sessions/share", { session_type: sessionType, session_id: sessionId, friend_ids: picked });
      toast.success(t("share.sent_toast"));
      onClose();
    } catch { toast.error(t("social.error")); }
    finally { setSending(false); }
  };

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 70, background: "var(--bl-overlay)", display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-sm glass-panel rounded-2xl p-5 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-black text-xl uppercase tracking-tight">{t("share.title")}</h2>
          <button onClick={onClose} className="p-1 text-zinc-500 hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        {friends.length === 0 ? (
          <p className="text-sm text-zinc-500">{t("share.none")}</p>
        ) : (
          <>
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">{t("share.pick")}</p>
            <div className="flex-1 overflow-y-auto space-y-2">
              {friends.map((f) => {
                const on = picked.includes(f.user_id);
                return (
                  <button key={f.user_id} onClick={() => toggle(f.user_id)}
                    className={`w-full flex items-center gap-3 rounded-md px-3 py-2.5 border text-left transition-colors ${on ? "bg-cyan-400/10 border-cyan-400/50" : "bg-zinc-900/50 border-zinc-800 hover:border-zinc-700"}`}>
                    <div className="h-9 w-9 rounded-full bg-zinc-900 overflow-hidden shrink-0">
                      <img src={av(f.picture)} alt="" className="h-full w-full object-cover" />
                    </div>
                    <span className="flex-1 min-w-0 font-bold text-sm truncate">{f.nickname}</span>
                    <span className={`h-5 w-5 rounded border flex items-center justify-center shrink-0 ${on ? "bg-cyan-400 border-cyan-400" : "border-zinc-700"}`}>
                      {on && <Check className="h-3.5 w-3.5 text-black" />}
                    </span>
                  </button>
                );
              })}
            </div>
            <button onClick={send} disabled={!picked.length || sending}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-cyan-400 text-black hover:bg-cyan-300 disabled:opacity-40 font-bold uppercase tracking-wide py-3 rounded-md">
              <Send className="h-4 w-4" /> {t("share.send")}
            </button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
